import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button, TypographyProps, SxProps, Theme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { getBackgroundColor, getContentColor } from '/packages/shared-components/utils/color';

export interface SnackbarProps {
  content: string | React.ReactNode;
  status?: 'warning' | 'error' | 'success' | 'info' | 'primary';
  action?: () => void;
  actionLabel?: string;
  variant?: 'solid' | 'subtle';
  textTransform?: TypographyProps['textTransform'];
  sx?: SxProps<Theme>;
  focus?: boolean;
}

// Design System Component: Atoms > Snackbar

const Snackbar = ({
  content,
  status = 'success',
  action,
  actionLabel = 'action',
  variant = 'solid',
  textTransform,
  sx = [],
  focus = false,
}: SnackbarProps) => {
  const divRef = useRef<HTMLDivElement | null>(null);
  const isSubtle = variant === 'subtle';
  const pl = isSubtle ? 2 : 2.75;
  const bgcolor = `${status}.main`;

  const getIcon = () => {
    switch (status) {
      case 'error':
      case 'warning':
        return <ErrorOutlineIcon fontSize="xs" color="inherit" />;
        break;
      case 'primary':
        return null;
        break;
      default:
        return <InfoOutlinedIcon fontSize="xs" color="inherit" />;
    }
  };

  useEffect(() => {
    if (focus && divRef.current) divRef.current.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <Box
      width="100%"
      py={1.75}
      pl={pl}
      pr={2.75}
      display="flex"
      justifyContent="space-between"
      bgcolor={!isSubtle ? bgcolor : undefined}
      ref={divRef}
      sx={[
        (theme) => ({
          background: isSubtle ? getBackgroundColor(theme.palette[status]) : undefined,
          color: isSubtle ? getContentColor(theme.palette[status]) : theme.palette[status].contrastText,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box display="flex" alignItems="start" gap={1}>
        {variant === 'subtle' && getIcon()}
        {typeof content === 'string' ? (
          <Typography variant="body2" lineHeight="20px" textTransform={textTransform} data-cy="info-snackbar">
            <span dangerouslySetInnerHTML={{ __html: content || '' }}></span>
          </Typography>
        ) : (
          content
        )}
      </Box>
      {action && (
        <Button variant="text" sx={{ lineHeight: 1, margin: '-6px -.5rem' }}>
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default Snackbar;
